/**
 * Lists Hooks - React Query Adapter
 *
 * Phase 4A: Single source of truth for list read state.
 * Phase 4B: React Query mutations with optimistic updates.
 * React Query IS the cache - no bespoke state.
 *
 * Query Hooks:
 * - useMyWatchlists(): Query for user's owned + collab lists
 * - useWatchlistDetail(): Query for single list metadata + first page
 * - useWatchlistItemsInfinite(): Infinite query for paginated list items
 *
 * Mutation Hooks (Phase 4B):
 * - useToggleListItemMutation(): Add/remove items from custom lists
 * - useCreateWatchlistMutation(): Create new watchlists
 * - useUpdateListItemNoteMutation(): Update item notes
 * - useReorderListItemsMutation(): Drag-and-drop reorder
 *
 * Cache Invalidation:
 * - useDefaultListCacheInvalidation(): For UserLibraryContext default list mutations
 */

import { useRef } from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { queryKeys } from '../lib/queryKeys';
import { apiFetch } from '../lib/api';

// Stable empty array reference to prevent infinite re-render loops
// when query.data is undefined (new users, deactivated users, etc.)
const EMPTY_LISTS: WatchlistMeta[] = [];

// ============================================================================
// Types (exported for use in components)
// ============================================================================

export type MediaType = 'movie' | 'tv';
export type MediaFilter = 'movie' | 'tv' | 'mixed';
export type CollaboratorRole = 'editor' | 'viewer';

export interface WatchlistMeta {
  id: string;
  name: string;
  slug: string | null;
  description?: string | null;
  visibility: 'public' | 'friends' | 'private';
  list_type?: 'watchlist' | 'watched' | 'favorites' | 'custom';
  is_default: boolean;
  cover_url?: string | null;
  item_count: number;
  follower_count?: number;
  is_official?: boolean;
  media_filter: MediaFilter;
  created_at?: string;
  user_id?: string;
  collaborator_role?: CollaboratorRole | null;
  has_collaborators?: boolean;
  revision?: number;
  updated_at?: string;
  owner?: {
    id: string;
    username?: string;
    display_name?: string;
    avatar_url?: string | null;
  };
}

export interface WatchlistItemRow {
  id: string;
  watchlist_id: string;
  media_id: number;
  media_type: MediaType;
  media_title: string;
  media_poster?: string | null;
  media_year?: number | null;
  notes?: string | null;
  position: number | null;
  rank?: string | null;
  added_at: string;
  // Optional enrichment fields from server-side Meilisearch lookup
  genre_ids?: number[];
  runtime?: number | null;
  imdb_rating?: number;
  rt_rating?: number;
  filmpl_rating?: number;
  popularity?: number;
  original_language?: string;
  vote_count?: number;
  // Attribution (populated for shared lists, null for legacy items)
  added_by_user_id?: string | null;
  added_by_username?: string | null;
}

interface MyWatchlistsResponse {
  watchlists: WatchlistMeta[];
}

interface WatchlistDetailResponse {
  watchlist: WatchlistMeta;
  items: WatchlistItemRow[];
  pagination: {
    page: number;
    limit: number;
    total: number | null;
    has_more: boolean;
  };
}

// ============================================================================
// LocalStorage cache helpers (for cache-first instant render)
// ============================================================================

const MY_LISTS_CACHE_PREFIX = 'filmple:myLists:';
const MY_LISTS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Optimistic guard type for updated_at-based reconciliation
// Stored in React Query cache (user-scoped, garbage-collectable)
interface OptimisticGuard {
  minUpdatedAtMs: number; // Server updated_at must be >= this to accept server count
  optimisticCount: number; // Our optimistic count to preserve if server is stale
}
type OptimisticGuards = Record<string, OptimisticGuard>;

interface CachedMyLists {
  lists: WatchlistMeta[];
  timestamp: number;
}

function getMyListsFromCache(userId: string): CachedMyLists | null {
  try {
    const raw = localStorage.getItem(`${MY_LISTS_CACHE_PREFIX}${userId}`);
    if (!raw) return null;
    const cached = JSON.parse(raw) as CachedMyLists;
    if (Date.now() - cached.timestamp > MY_LISTS_CACHE_TTL_MS) {
      localStorage.removeItem(`${MY_LISTS_CACHE_PREFIX}${userId}`);
      return null;
    }
    return cached;
  } catch {
    return null;
  }
}

function setMyListsCache(userId: string, lists: WatchlistMeta[]): void {
  try {
    const cached: CachedMyLists = { lists, timestamp: Date.now() };
    localStorage.setItem(`${MY_LISTS_CACHE_PREFIX}${userId}`, JSON.stringify(cached));
  } catch {
    // Ignore storage errors
  }
}

// ============================================================================
// Optimistic Guard Helpers (updated_at-based reconciliation, no TTL heuristics)
// ============================================================================

/**
 * Get the optimistic guards query key for a user
 * This stores guards in React Query cache (user-scoped, garbage-collectable)
 */
function getOptimisticGuardsKey(userId: string) {
  return ['lists', 'optimisticGuards', userId] as const;
}

/**
 * Set an optimistic guard for a list after applying an optimistic count delta.
 * The guard records the minimum server updated_at we need to see before accepting server counts.
 *
 * FIX C: Use server's current updated_at as baseline instead of Date.now() to avoid clock-skew issues.
 * Server timestamp is monotonic and guaranteed to increase after each mutation.
 */
function setOptimisticGuard(
  queryClient: QueryClient,
  userId: string,
  listId: string,
  optimisticCount: number,
  currentServerUpdatedAt: string | undefined
): void {
  const key = getOptimisticGuardsKey(userId);
  // Use server's current updated_at as baseline (monotonic, no clock-skew)
  // Add 1ms to ensure we only accept strictly newer server timestamps
  const baselineMs = currentServerUpdatedAt
    ? new Date(currentServerUpdatedAt).getTime()
    : 0;
  queryClient.setQueryData<OptimisticGuards>(key, (old) => ({
    ...old,
    [listId]: {
      minUpdatedAtMs: baselineMs + 1,
      optimisticCount,
    },
  }));
}

/**
 * Clear the optimistic guard for a list (when server data is fresh enough)
 */
function clearOptimisticGuard(
  queryClient: QueryClient,
  userId: string,
  listId: string
): void {
  const key = getOptimisticGuardsKey(userId);
  queryClient.setQueryData<OptimisticGuards>(key, (old) => {
    if (!old || !old[listId]) return old;
    const { [listId]: _, ...rest } = old;
    return rest;
  });
}

/**
 * Reconcile server lists with optimistic guards using monotonic updated_at.
 * If server.updated_at >= guard.minUpdatedAtMs, accept server count and clear guard.
 * Otherwise, preserve optimistic count.
 */
function reconcileWithOptimisticGuards(
  queryClient: QueryClient,
  userId: string,
  serverLists: WatchlistMeta[]
): WatchlistMeta[] {
  const key = getOptimisticGuardsKey(userId);
  const guards = queryClient.getQueryData<OptimisticGuards>(key) || {};

  // If no guards, return server data as-is
  if (Object.keys(guards).length === 0) return serverLists;

  return serverLists.map((serverList) => {
    const guard = guards[serverList.id];
    if (!guard) return serverList;

    // Parse server updated_at to ms timestamp
    const serverUpdatedAtMs = serverList.updated_at
      ? new Date(serverList.updated_at).getTime()
      : 0;

    if (serverUpdatedAtMs >= guard.minUpdatedAtMs) {
      // Server is fresh enough - accept server count and clear guard
      clearOptimisticGuard(queryClient, userId, serverList.id);
      return serverList;
    } else {
      // Server is stale - preserve optimistic count
      return { ...serverList, item_count: guard.optimisticCount };
    }
  });
}

// ============================================================================
// useMyWatchlists - Query hook for user's owned + collab lists
// ============================================================================

interface UseMyWatchlistsResult {
  lists: WatchlistMeta[];
  myLists: WatchlistMeta[];
  collabLists: WatchlistMeta[];
  isLoading: boolean;
  isHydrated: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to get all watchlists the user owns or collaborates on.
 * Single source of truth for Sidebar lists.
 * Cache-first from localStorage for instant render.
 */
export function useMyWatchlists(): UseMyWatchlistsResult {
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;

  // Get cached data for initialData (cache-first pattern)
  const cached = userId ? getMyListsFromCache(userId) : null;

  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.lists.mine(userId || ''),
    // FIX: Accept signal for abort support - prevents stale responses from overwriting optimistic cache
    queryFn: async ({ signal }) => {
      if (!token) return [];

      const data = await apiFetch<MyWatchlistsResponse>('/watchlists', { token, signal });
      const serverLists = data.watchlists || [];

      // Reconcile server data with optimistic guards using monotonic updated_at
      // This prevents stale server counts from overwriting optimistic counts
      const lists = userId
        ? reconcileWithOptimisticGuards(queryClient, userId, serverLists)
        : serverLists;

      // Update localStorage cache on success (with reconciled counts for consistency)
      if (userId) {
        setMyListsCache(userId, lists);
      }

      return lists;
    },
    enabled: !!userId && !!token,
    staleTime: 60_000, // 1 minute
    initialData: cached?.lists,
    initialDataUpdatedAt: cached?.timestamp,
  });

  // Classify lists by ownership
  const lists = query.data ?? EMPTY_LISTS;
  const myLists = lists.filter((l) => l.is_default || l.user_id === userId);
  const collabLists = lists.filter((l) => !l.is_default && l.user_id !== userId && l.collaborator_role);

  return {
    lists,
    myLists,
    collabLists,
    isLoading: query.isLoading,
    // Hydrated = we have usable data (from cache/initialData) OR we've completed a fetch
    // This correctly handles:
    // - initialData from cache: data !== undefined → true (instant render)
    // - first fetch complete: isFetched → true
    // - disabled query with no cache: false (don't render empty prematurely)
    isHydrated: query.data !== undefined || query.isFetched,
    error: query.error as Error | null,
    refetch: async () => { await query.refetch(); },
  };
}

// ============================================================================
// useWatchlistDetail - Query hook for single list metadata + first page
// ============================================================================

interface UseWatchlistDetailOptions {
  enabled?: boolean;
}

interface UseWatchlistDetailResult {
  watchlist: WatchlistMeta | null;
  items: WatchlistItemRow[];
  pagination: {
    page: number;
    limit: number;
    total: number | null;
    hasMore: boolean;
  };
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to get a single watchlist's metadata and first page of items.
 * Supports both authenticated and unauthenticated access (public lists).
 */
export function useWatchlistDetail(
  listId: string | undefined,
  options: UseWatchlistDetailOptions = {}
): UseWatchlistDetailResult {
  const { session } = useAuth();
  const token = session?.access_token;
  const { enabled = true } = options;

  const query = useQuery({
    queryKey: queryKeys.lists.detail(listId || ''),
    queryFn: async () => {
      if (!listId) return null;

      const data = await apiFetch<WatchlistDetailResponse>(
        `/watchlists/${listId}?page=1&limit=100&sort_by=position&sort_order=asc`,
        { token }
      );

      return data;
    },
    enabled: enabled && !!listId,
    staleTime: 30_000, // 30 seconds
  });

  const data = query.data;

  return {
    watchlist: data?.watchlist ?? null,
    items: data?.items ?? [],
    pagination: {
      page: data?.pagination?.page ?? 1,
      limit: data?.pagination?.limit ?? 100,
      total: data?.pagination?.total ?? null,
      hasMore: data?.pagination?.has_more ?? false,
    },
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    refetch: async () => { await query.refetch(); },
  };
}

// ============================================================================
// useWatchlistItemsInfinite - Infinite query for paginated list items
// ============================================================================

type SortBy = 'position' | 'added_at' | 'media_title' | 'media_year';
type SortOrder = 'asc' | 'desc';

interface UseWatchlistItemsInfiniteOptions {
  enabled?: boolean;
  limit?: number;
  sortBy?: SortBy;
  sortOrder?: SortOrder;
}

interface UseWatchlistItemsInfiniteResult {
  watchlist: WatchlistMeta | null;
  items: WatchlistItemRow[];
  total: number | null;
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  error: Error | null;
  fetchNextPage: () => Promise<void>;
  refetch: () => Promise<void>;
}

/**
 * Hook for infinite scroll list items.
 * Supports configurable sort order via sortBy/sortOrder options.
 * Default: added_at desc (newest first) - so new items appear at top.
 * This is the PRIMARY hook for WatchlistPage - includes watchlist metadata.
 */
export function useWatchlistItemsInfinite(
  listId: string | undefined,
  options: UseWatchlistItemsInfiniteOptions = {}
): UseWatchlistItemsInfiniteResult {
  const { session } = useAuth();
  const token = session?.access_token;
  const {
    enabled = true,
    limit = 100,
    sortBy = 'added_at',
    sortOrder = 'desc',
  } = options;

  // Include sort params in query key to ensure different sorts have different cache entries
  const queryKey = [...queryKeys.lists.itemsBase(listId || ''), { sortBy, sortOrder }];

  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      if (!listId) {
        return { items: [], pagination: { page: 1, limit, total: null, has_more: false }, watchlist: null };
      }

      const data = await apiFetch<WatchlistDetailResponse>(
        `/watchlists/${listId}?page=${pageParam}&limit=${limit}&sort_by=${sortBy}&sort_order=${sortOrder}`,
        { token }
      );

      return {
        items: data.items || [],
        pagination: data.pagination || { page: pageParam, limit, total: null, has_more: false },
        watchlist: data.watchlist,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage.pagination.has_more) return undefined;
      return lastPage.pagination.page + 1;
    },
    enabled: enabled && !!listId,
    staleTime: 30_000,
    refetchOnMount: true, // Override global false: revalidate stale order after hard refresh
  });

  // Flatten all pages into a single items array
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];

  // Get watchlist metadata from first page
  const watchlist = query.data?.pages[0]?.watchlist ?? null;

  // Get total from first page pagination
  const total = query.data?.pages[0]?.pagination.total ?? null;

  return {
    watchlist,
    items,
    total,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    error: query.error as Error | null,
    fetchNextPage: async () => { await query.fetchNextPage(); },
    refetch: async () => { await query.refetch(); },
  };
}

// ============================================================================
// useCustomListMembershipBatch - Batch membership check for custom lists
// ============================================================================

interface BatchMembershipResponse {
  in_list_ids: string[];
}

interface UseCustomListMembershipBatchOptions {
  enabled?: boolean;
}

interface UseCustomListMembershipBatchResult {
  inListIds: Set<string>;
  isLoading: boolean;
  error: Error | null;
  isInList: (listId: string) => boolean;
  refetch: () => Promise<void>;
}

/**
 * Hook to check which custom lists contain a specific media item.
 * Single source of truth for membership - replaces per-list item fetching.
 * Returns a Set of list IDs that contain the item.
 */
export function useCustomListMembershipBatch(
  mediaId: number | undefined,
  mediaType: MediaType | undefined,
  listIds: string[],
  options: UseCustomListMembershipBatchOptions = {}
): UseCustomListMembershipBatchResult {
  const { session } = useAuth();
  const token = session?.access_token;
  const { enabled = true } = options;

  // Sort and join for stable query key
  const sortedListIds = [...listIds].sort();
  const hasValidInput = mediaId !== undefined && mediaType !== undefined && listIds.length > 0;

  const query = useQuery({
    queryKey: queryKeys.lists.membership(mediaId ?? 0, mediaType ?? 'movie', sortedListIds),
    // FIX: Accept signal for abort support - prevents stale responses from overwriting optimistic cache
    queryFn: async ({ signal }) => {
      if (!token || !hasValidInput) return { in_list_ids: [] };

      const data = await apiFetch<BatchMembershipResponse>(
        '/watchlists/membership/batch',
        {
          method: 'POST',
          token,
          signal, // Pass signal to abort in-flight requests on cancel
          body: {
            list_ids: sortedListIds,
            media_id: mediaId,
            media_type: mediaType,
          },
        }
      );

      return data;
    },
    enabled: enabled && !!token && hasValidInput,
    // D2: Always revalidate when dropdown opens (no stale membership after refresh)
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const inListIds = new Set(query.data?.in_list_ids ?? []);

  return {
    inListIds,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    isInList: (listId: string) => inListIds.has(listId),
    refetch: async () => { await query.refetch(); },
  };
}

// ============================================================================
// useCustomListMembershipLookup - Lookup with item_ids for removal
// ============================================================================

interface MembershipLookupMatch {
  list_id: string;
  item_id: string;
  added_at: string;
}

interface MembershipLookupResponse {
  matches: MembershipLookupMatch[];
}

interface UseCustomListMembershipLookupOptions {
  enabled?: boolean;
}

interface UseCustomListMembershipLookupResult {
  matches: MembershipLookupMatch[];
  isLoading: boolean;
  error: Error | null;
  getItemId: (listId: string) => string | undefined;
  refetch: () => Promise<void>;
}

/**
 * Hook to lookup item_ids for a media item across multiple lists.
 * Used for removal when the list items haven't been fetched (no page 1 cache).
 * Returns item_id per list for DELETE operations.
 */
export function useCustomListMembershipLookup(
  mediaId: number | undefined,
  mediaType: MediaType | undefined,
  listIds: string[],
  options: UseCustomListMembershipLookupOptions = {}
): UseCustomListMembershipLookupResult {
  const { session } = useAuth();
  const token = session?.access_token;
  const { enabled = true } = options;

  // Sort and join for stable query key
  const sortedListIds = [...listIds].sort();
  const hasValidInput = mediaId !== undefined && mediaType !== undefined && listIds.length > 0;

  const query = useQuery({
    queryKey: ['lists', 'membershipLookup', mediaId ?? 0, mediaType ?? 'movie', sortedListIds],
    queryFn: async () => {
      if (!token || !hasValidInput) return { matches: [] };

      const data = await apiFetch<MembershipLookupResponse>(
        '/watchlists/membership/lookup',
        {
          method: 'POST',
          token,
          body: {
            list_ids: sortedListIds,
            media_id: mediaId,
            media_type: mediaType,
          },
        }
      );

      return data;
    },
    enabled: enabled && !!token && hasValidInput,
    staleTime: 60_000, // 1 minute
  });

  const matches = query.data?.matches ?? [];
  const itemIdByListId = new Map(matches.map((m) => [m.list_id, m.item_id]));

  return {
    matches,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    getItemId: (listId: string) => itemIdByListId.get(listId),
    refetch: async () => { await query.refetch(); },
  };
}

// ============================================================================
// Utility hooks for ListsContext adapter
// ============================================================================

/**
 * Get a specific list's metadata from cache or fetch.
 * Used by ListsContext.getListMeta adapter.
 */
export function useWatchlistMetaFromCache(listId: string | undefined): WatchlistMeta | null {
  const queryClient = useQueryClient();

  if (!listId) return null;

  // Try detail cache first
  const detailData = queryClient.getQueryData<WatchlistDetailResponse>(
    queryKeys.lists.detail(listId)
  );
  if (detailData?.watchlist) return detailData.watchlist;

  // Try infinite query cache (any sort variant - watchlist is included in each page)
  const infiniteData = getAnyListItemsData(queryClient, listId);
  if (infiniteData?.pages?.[0]?.watchlist) return infiniteData.pages[0].watchlist;

  return null;
}

/**
 * Seed a list's detail cache with pre-fetched data.
 * Used by ListsContext.seedFromWatchlistDetail adapter.
 */
export function useSeedWatchlistDetail() {
  const queryClient = useQueryClient();

  return (listId: string, data: WatchlistDetailResponse) => {
    queryClient.setQueryData(queryKeys.lists.detail(listId), data);
  };
}

/**
 * Invalidate list-related queries.
 * Uses predicate-based invalidation to target ALL sort variants.
 */
export function useInvalidateLists() {
  const queryClient = useQueryClient();

  return {
    invalidateMine: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.lists.mineBase() });
    },
    invalidateDetail: async (listId: string) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.lists.detail(listId) });
    },
    invalidateItems: async (listId: string) => {
      // Invalidate ALL sort variants for this list's items
      invalidateAllListItemsQueries(queryClient, listId);
    },
    invalidateAll: async (listId: string) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.lists.detail(listId) });
      invalidateAllListItemsQueries(queryClient, listId);
    },
  };
}

/**
 * TASK 1: Invalidate membership-related queries after toggle.
 * Forces UI to re-fetch server membership state so it converges quickly.
 */
export function useInvalidateMembership() {
  const queryClient = useQueryClient();

  return async (mediaId: number, mediaType: MediaType) => {
    // Invalidate batch membership queries for this media
    await queryClient.invalidateQueries({
      queryKey: ['lists', 'membership', mediaId, mediaType],
      exact: false,
    });
    // Invalidate lookup queries for this media
    await queryClient.invalidateQueries({
      queryKey: ['lists', 'membershipLookup', mediaId, mediaType],
      exact: false,
    });
  };
}

// ============================================================================
// Optimistic Update Utilities (Phase 4A - for mutations to update RQ cache)
// ============================================================================

type InfiniteItemsData = {
  pages: Array<{
    items: WatchlistItemRow[];
    pagination: { page: number; limit: number; total: number | null; has_more: boolean };
    watchlist: WatchlistMeta | null;
  }>;
  pageParams: number[];
};

// ============================================================================
// Cache Key Consistency Helpers
// ============================================================================
//
// IMPORTANT: List items queries now include sort params in the key:
//   ['lists', listId, 'items', { sortBy, sortOrder }]
// But the base key is: ['lists', listId, 'items']
//
// These helpers ensure mutations update ALL cached sort variants for a list.
// Without this, mutations write to wrong cache entry, causing "appear then disappear".

/**
 * Predicate to match all list-items query variants for a given listId.
 * Matches: ['lists', listId, 'items'] and ['lists', listId, 'items', { sortBy, sortOrder }]
 */
export function isListItemsQuery(queryKey: readonly unknown[], listId: string): boolean {
  return (
    Array.isArray(queryKey) &&
    queryKey.length >= 3 &&
    queryKey[0] === 'lists' &&
    queryKey[1] === listId &&
    queryKey[2] === 'items'
  );
}

/**
 * Get cached list-items data for a list, choosing the freshest variant by dataUpdatedAt.
 * When multiple sort variants exist, returns the one with the newest dataUpdatedAt
 * to ensure deterministic behavior for membership/pending checks.
 */
export function getAnyListItemsData(
  queryClient: QueryClient,
  listId: string
): InfiniteItemsData | undefined {
  const queries = queryClient.getQueryCache().findAll({
    predicate: (query) => isListItemsQuery(query.queryKey, listId),
  });

  // Find the variant with the newest dataUpdatedAt among those with data
  let freshest: { data: InfiniteItemsData; updatedAt: number } | undefined;
  for (const query of queries) {
    const data = query.state.data as InfiniteItemsData | undefined;
    if (data && query.state.dataUpdatedAt) {
      if (!freshest || query.state.dataUpdatedAt > freshest.updatedAt) {
        freshest = { data, updatedAt: query.state.dataUpdatedAt };
      }
    }
  }
  return freshest?.data;
}

/**
 * Update ALL cached list-items query variants for a list.
 * Ensures optimistic updates apply regardless of which sort order is cached.
 */
export function setAllListItemsData(
  queryClient: QueryClient,
  listId: string,
  updater: (old: InfiniteItemsData | undefined) => InfiniteItemsData | undefined
): void {
  queryClient.setQueriesData<InfiniteItemsData>(
    { predicate: (query) => isListItemsQuery(query.queryKey, listId) },
    updater
  );
}

/**
 * Sort params extracted from a list-items query key.
 * queryKey[3] is `{ sortBy, sortOrder }` when present.
 */
export interface ListItemsSortParams {
  sortBy?: string;
  sortOrder?: string;
}

/**
 * Update ALL cached list-items query variants, passing the sort params from each
 * variant's query key to the updater. This enables variant-aware optimistic placement
 * (e.g., append for Custom Order, prepend for Date Added desc).
 */
export function setAllListItemsDataWithSortInfo(
  queryClient: QueryClient,
  listId: string,
  updater: (old: InfiniteItemsData | undefined, sortParams: ListItemsSortParams) => InfiniteItemsData | undefined
): void {
  const queries = queryClient.getQueryCache().findAll({
    predicate: (query) => isListItemsQuery(query.queryKey, listId),
  });

  for (const query of queries) {
    const sortParams: ListItemsSortParams =
      (query.queryKey[3] as ListItemsSortParams) || {};
    queryClient.setQueryData<InfiniteItemsData>(
      query.queryKey,
      (old) => updater(old, sortParams)
    );
  }
}

/**
 * Cancel all in-flight list-items queries for a list.
 */
export async function cancelAllListItemsQueries(
  queryClient: QueryClient,
  listId: string
): Promise<void> {
  await queryClient.cancelQueries({
    predicate: (query) => isListItemsQuery(query.queryKey, listId),
  });
}

/**
 * Invalidate all list-items query variants for a list.
 */
export function invalidateAllListItemsQueries(
  queryClient: QueryClient,
  listId: string
): void {
  queryClient.invalidateQueries({
    predicate: (query) => isListItemsQuery(query.queryKey, listId),
  });
}

/**
 * Hook for optimistic list item updates.
 * Mutations should call these to keep React Query cache in sync.
 * Uses setAllListItemsData to update ALL sort variants of a list's items cache.
 */
export function useOptimisticListUpdates() {
  const queryClient = useQueryClient();

  const addItem = (listId: string, item: WatchlistItemRow) => {
    setAllListItemsDataWithSortInfo(queryClient, listId, (old, sortParams) => {
      if (!old) return old;

      // Check if item already exists in ANY page
      const existsInAnyPage = old.pages.some((page) =>
        page.items.some((i) => i.media_id === item.media_id && i.media_type === item.media_type)
      );
      if (existsInAnyPage) {
        // MERGE existing item to preserve enrichment fields (don't duplicate)
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((i) =>
              i.media_id === item.media_id && i.media_type === item.media_type
                ? { ...i, ...item }
                : i
            ),
          })),
        };
      }

      // Variant-aware placement: match canonical server placement
      const shouldAppend =
        sortParams.sortBy === 'position' ||
        (sortParams.sortBy === 'added_at' && sortParams.sortOrder === 'asc');

      const lastIdx = old.pages.length - 1;
      const lastPageFullyLoaded = !old.pages[lastIdx]?.pagination.has_more;

      const newPages = old.pages.map((page, idx) => {
        let newItems = page.items;

        if (shouldAppend) {
          // Append variants: insert on last page ONLY if fully loaded
          if (idx === lastIdx && lastPageFullyLoaded) {
            newItems = [...page.items, item];
          }
          // If not fully loaded, skip insert (rely on refetch/realtime)
        } else {
          // Prepend variants: insert on first page
          if (idx === 0) {
            newItems = [item, ...page.items];
          }
        }

        // Update item_count on page 0's watchlist meta
        const updatedWatchlist = idx === 0 && page.watchlist
          ? { ...page.watchlist, item_count: (page.watchlist.item_count || 0) + 1 }
          : page.watchlist;

        return { ...page, items: newItems, watchlist: updatedWatchlist };
      });

      return { ...old, pages: newPages };
    });
  };

  const removeItem = (listId: string, mediaId: number, mediaType: MediaType) => {
    setAllListItemsData(queryClient, listId, (old) => {
      if (!old) return old;
      const newPages = old.pages.map((page, idx) => {
        const filtered = page.items.filter(
          (i) => !(i.media_id === mediaId && i.media_type === mediaType)
        );
        const removed = filtered.length < page.items.length;
        return {
          ...page,
          items: filtered,
          watchlist:
            removed && page.watchlist && idx === 0
              ? { ...page.watchlist, item_count: Math.max(0, (page.watchlist.item_count || 0) - 1) }
              : page.watchlist,
        };
      });
      return { ...old, pages: newPages };
    });
  };

  const reorderItems = (listId: string, orderedItems: WatchlistItemRow[]) => {
    // Part 1: Update ONLY position/asc variant with new order
    const queries = queryClient.getQueryCache().findAll({
      predicate: (query) => isListItemsQuery(query.queryKey, listId),
    });

    for (const query of queries) {
      const sortParams = (query.queryKey[3] as ListItemsSortParams) || {};
      if (sortParams.sortBy === 'position' && sortParams.sortOrder === 'asc') {
        // Canonical variant: rewrite items in new order
        queryClient.setQueryData<InfiniteItemsData>(query.queryKey, (old) => {
          if (!old || old.pages.length === 0) return old;
          const orderMap = new Map<string, number>();
          orderedItems.forEach((item, idx) => {
            orderMap.set(`${item.media_type}:${item.media_id}`, idx);
          });
          const flat = old.pages.flatMap((page) => page.items);
          flat.sort((a, b) => {
            const aIdx = orderMap.get(`${a.media_type}:${a.media_id}`) ?? Infinity;
            const bIdx = orderMap.get(`${b.media_type}:${b.media_id}`) ?? Infinity;
            return aIdx - bIdx;
          });
          let offset = 0;
          const newPages = old.pages.map((page) => {
            const pageSize = page.items.length;
            const slice = flat.slice(offset, offset + pageSize);
            offset += pageSize;
            return { ...page, items: slice };
          });
          return { ...old, pages: newPages };
        });
      } else {
        // Non-canonical variant: invalidate so it refetches cleanly
        queryClient.invalidateQueries({ queryKey: query.queryKey });
      }
    }
  };

  const updateItemNote = (listId: string, mediaId: number, mediaType: MediaType, note: string | null) => {
    setAllListItemsData(queryClient, listId, (old) => {
      if (!old) return old;
      const newPages = old.pages.map((page) => ({
        ...page,
        items: page.items.map((i) =>
          i.media_id === mediaId && i.media_type === mediaType ? { ...i, notes: note } : i
        ),
      }));
      return { ...old, pages: newPages };
    });
  };

  return { addItem, removeItem, reorderItems, updateItemNote };
}

// ============================================================================
// React Query Mutation Hooks (Phase 4B)
// ============================================================================

// TASK 3: Enrichment fields for instant metadata display
export interface MediaEnrichment {
  genre_ids?: number[];
  runtime?: number | null;
  imdb_rating?: number;
  rt_rating?: number;
  filmpl_rating?: number;
  popularity?: number;
  original_language?: string;
  vote_count?: number;
}

interface ToggleListItemParams {
  listId: string;
  mediaId: number;
  mediaType: MediaType;
  mediaTitle: string;
  mediaPoster?: string;
  mediaYear?: number;
  // CRITICAL: These must be computed PRE-OPTIMISTIC at the call site
  // mutationFn uses these directly - NEVER re-reads cache
  isAdd: boolean; // true = add, false = remove
  confirmedItemId?: string; // For removal - the item's confirmed server UUID (NOT temp-)
  // TASK 3: Optional enrichment fields for instant metadata display
  enrichment?: MediaEnrichment;
}

interface ToggleListItemContext {
  previousData: InfiniteItemsData | undefined;
  wasInList: boolean;
  // A: Snapshot membership batch queries for rollback on error
  previousMembershipQueries: Array<[readonly unknown[], BatchMembershipResponse | undefined]>;
}

/**
 * Mutation hook for toggling items in custom lists (add/remove).
 * Implements optimistic updates with rollback on error.
 * Uses cache helpers to update ALL sort variants of the list-items query.
 *
 * Pattern: cancelQueries → snapshot → optimistic setQueryData → rollback on error → onSettled invalidate
 */
export function useToggleListItemMutation() {
  const { session, user } = useAuth();
  const token = session?.access_token;
  const queryClient = useQueryClient();

  return useMutation<
    { item?: WatchlistItemRow; removed?: boolean; duplicate?: boolean },
    Error,
    ToggleListItemParams,
    ToggleListItemContext
  >({
    mutationFn: async ({ listId, mediaId, mediaType, mediaTitle, mediaPoster, mediaYear, isAdd, confirmedItemId }) => {
      if (!token) throw new Error('Not authenticated');

      // CRITICAL: Use isAdd/confirmedItemId from variables (computed PRE-OPTIMISTIC at call site)
      // NEVER re-read cache here - onMutate has already modified it with temp items!

      if (isAdd) {
        // Add item - POST to server
        const data = await apiFetch<{ item: WatchlistItemRow; duplicate?: boolean }>(
          `/watchlists/${listId}/items`,
          {
            method: 'POST',
            token,
            body: {
              media_id: mediaId,
              media_type: mediaType,
              media_title: mediaTitle,
              media_poster: mediaPoster,
              media_year: mediaYear,
            },
          }
        );
        return { item: data.item, duplicate: data.duplicate };
      } else {
        // Remove item - DELETE from server
        // confirmedItemId MUST be a real server UUID (not temp-)
        if (!confirmedItemId) {
          throw new Error('Cannot remove item without confirmedItemId');
        }
        if (confirmedItemId.startsWith('temp-')) {
          // Safety guard - should never happen if call site is correct
          throw new Error('Cannot remove item with temp ID - wait for add to confirm');
        }
        await apiFetch(`/watchlists/${listId}/items/${confirmedItemId}`, {
          method: 'DELETE',
          token,
        });
        return { removed: true };
      }
    },

    onMutate: async ({ listId, mediaId, mediaType, mediaTitle, mediaPoster, mediaYear, isAdd, enrichment }) => {
      // Cancel any outgoing refetches for ALL sort variants
      await cancelAllListItemsQueries(queryClient, listId);

      // Snapshot current data from any cached variant (for rollback)
      const previousData = getAnyListItemsData(queryClient, listId);

      // wasInList is the inverse of isAdd (computed at call site from pre-optimistic cache)
      const wasInList = !isAdd;

      // Optimistic update - apply to ALL cached sort variants (variant-aware placement)
      setAllListItemsDataWithSortInfo(queryClient, listId, (old, sortParams) => {
        // Seed minimal cache if not present (enables membership checks before list is fetched)
        if (!old) {
          old = {
            pages: [{
              items: [],
              pagination: { page: 1, limit: 100, total: 0, has_more: false },
              watchlist: null,
            }],
            pageParams: [1],
          };
        }

        if (wasInList) {
          // Remove item optimistically (only for confirmed server items)
          const newPages = old.pages.map((page, idx) => {
            const filtered = page.items.filter(
              (i) => !(i.media_id === mediaId && i.media_type === mediaType)
            );
            const removed = filtered.length < page.items.length;
            return {
              ...page,
              items: filtered,
              watchlist:
                removed && page.watchlist && idx === 0
                  ? { ...page.watchlist, item_count: Math.max(0, (page.watchlist.item_count || 0) - 1) }
                  : page.watchlist,
            };
          });
          return { ...old, pages: newPages };
        } else {
          // Add item optimistically with variant-aware + last-page-aware placement
          const optimisticItem: WatchlistItemRow = {
            id: `temp-${Date.now()}`,
            watchlist_id: listId,
            media_id: mediaId,
            media_type: mediaType,
            media_title: mediaTitle,
            media_poster: mediaPoster ?? null,
            media_year: mediaYear ?? null,
            notes: null,
            position: 999999,
            added_at: new Date().toISOString(),
            genre_ids: enrichment?.genre_ids,
            runtime: enrichment?.runtime,
            imdb_rating: enrichment?.imdb_rating,
            rt_rating: enrichment?.rt_rating,
            filmpl_rating: enrichment?.filmpl_rating,
            popularity: enrichment?.popularity,
            original_language: enrichment?.original_language,
            vote_count: enrichment?.vote_count,
          };
          const shouldAppend =
            sortParams.sortBy === 'position' ||
            (sortParams.sortBy === 'added_at' && sortParams.sortOrder === 'asc');

          const lastIdx = old.pages.length - 1;
          const lastPageFullyLoaded = !old.pages[lastIdx]?.pagination.has_more;

          const newPages = old.pages.map((page, idx) => {
            let newItems = page.items;

            if (shouldAppend) {
              // Append: insert on last page ONLY if fully loaded
              if (idx === lastIdx && lastPageFullyLoaded) {
                newItems = [...page.items, optimisticItem];
              }
            } else {
              // Prepend: insert on first page
              if (idx === 0) {
                newItems = [optimisticItem, ...page.items];
              }
            }

            const updatedWatchlist = idx === 0 && page.watchlist
              ? { ...page.watchlist, item_count: (page.watchlist.item_count || 0) + 1 }
              : page.watchlist;

            return { ...page, items: newItems, watchlist: updatedWatchlist };
          });
          return { ...old, pages: newPages };
        }
      });

      // Also update the mine query's item_count (use user.id from AuthContext, not session)
      // FIX: On cold cache, fall back to localStorage cache for instant sidebar count update
      const userId = user?.id;
      let currentServerUpdatedAt: string | undefined;

      if (userId) {
        // Try React Query cache first
        let listsData = queryClient.getQueryData<WatchlistMeta[]>(queryKeys.lists.mine(userId));

        // If React Query cache is cold, try localStorage cache (used as initialData by useMyWatchlists)
        if (!listsData) {
          const cached = getMyListsFromCache(userId);
          if (cached?.lists) {
            listsData = cached.lists;
            // Seed React Query cache with localStorage data so setQueryData works
            queryClient.setQueryData(queryKeys.lists.mine(userId), listsData);
          }
        }

        // Only apply delta if we have a source of truth (never invent lists)
        if (listsData) {
          // Find current count and updated_at before delta for guard
          const currentList = listsData.find((l) => l.id === listId);
          const currentCount = currentList?.item_count ?? 0;
          currentServerUpdatedAt = currentList?.updated_at;
          const delta = wasInList ? -1 : 1;
          const newCount = Math.max(0, currentCount + delta);

          queryClient.setQueryData<WatchlistMeta[]>(
            queryKeys.lists.mine(userId),
            (old) => {
              if (!old) return old;
              return old.map((list) => {
                if (list.id === listId) {
                  return { ...list, item_count: newCount };
                }
                return list;
              });
            }
          );

          // FIX C: Set optimistic guard using server's current updated_at (no clock-skew)
          // This prevents stale server data from overwriting our optimistic count
          setOptimisticGuard(queryClient, userId, listId, newCount, currentServerUpdatedAt);
        }
        // If neither cache has data, skip - invalidateQueries in onSettled will fetch
      }

      // FIX: Cancel in-flight membership and mine queries BEFORE optimistic patching
      // This prevents stale responses from races with the mutation (on→off→on flicker)
      await queryClient.cancelQueries({
        queryKey: ['lists', 'membership', mediaId, mediaType],
        exact: false,
      });
      await queryClient.cancelQueries({
        queryKey: ['lists', 'membershipLookup', mediaId, mediaType],
        exact: false,
      });
      if (userId) {
        await queryClient.cancelQueries({ queryKey: queryKeys.lists.mine(userId) });
      }

      // FIX A: Patch membership batch cache for instant REMOVE active state
      // Snapshot current membership queries for rollback
      const previousMembershipQueries = queryClient.getQueriesData<BatchMembershipResponse>({
        queryKey: ['lists', 'membership', mediaId, mediaType],
        exact: false,
      });

      // Optimistically update ALL membership batch queries for this media
      queryClient.setQueriesData<BatchMembershipResponse>(
        { queryKey: ['lists', 'membership', mediaId, mediaType], exact: false },
        (old) => {
          // If no cached data, create minimal structure for optimistic patch
          const currentIds = old?.in_list_ids ?? [];

          if (isAdd) {
            // ADD: ensure listId is in the set
            if (!currentIds.includes(listId)) {
              return { in_list_ids: [...currentIds, listId] };
            }
          } else {
            // REMOVE: ensure listId is NOT in the set
            return { in_list_ids: currentIds.filter((id) => id !== listId) };
          }
          return old;
        }
      );

      return { previousData, wasInList, previousMembershipQueries };
    },

    onError: (_err, { listId }, context) => {
      // Rollback to snapshot - apply to ALL sort variants
      if (context?.previousData) {
        setAllListItemsData(queryClient, listId, () => context.previousData);
      }

      // FIX A: Rollback membership batch queries from snapshot
      if (context?.previousMembershipQueries) {
        for (const [key, data] of context.previousMembershipQueries) {
          queryClient.setQueryData(key, data);
        }
      }

      // Invalidate ALL sort variants to ensure consistency
      invalidateAllListItemsQueries(queryClient, listId);
    },

    onSuccess: (data, { listId, mediaId, mediaType }) => {
      // If we got the real item back, update ALL cached sort variants with server data
      // CRITICAL: Merge to preserve existing enrichment fields (genre_ids, runtime, ratings)
      // Server POST /watchlist-items returns core fields only, not enrichment
      if (data.item) {
        setAllListItemsData(queryClient, listId, (old) => {
          if (!old) return old;
          const newPages = old.pages.map((page) => ({
            ...page,
            items: page.items.map((i) => {
              if (i.media_id === mediaId && i.media_type === mediaType) {
                // Merge: spread cached item first (has enrichment), then server item (has real ID)
                // Then restore enrichment from cached if server didn't provide
                return {
                  ...i,
                  ...data.item!,
                  // Preserve enrichment fields from cached item if server returned undefined
                  genre_ids: data.item!.genre_ids ?? i.genre_ids,
                  runtime: data.item!.runtime ?? i.runtime,
                  imdb_rating: data.item!.imdb_rating ?? i.imdb_rating,
                  rt_rating: data.item!.rt_rating ?? i.rt_rating,
                  filmpl_rating: data.item!.filmpl_rating ?? i.filmpl_rating,
                  popularity: data.item!.popularity ?? i.popularity,
                  original_language: data.item!.original_language ?? i.original_language,
                  vote_count: data.item!.vote_count ?? i.vote_count,
                };
              }
              return i;
            }),
          }));
          return { ...old, pages: newPages };
        });
      }
    },

    onSettled: (_data, _error, { mediaId, mediaType }) => {
      // IMPORTANT: Do NOT invalidate itemsBase(listId) here!
      // For lists with >100 items, position-based page 1 won't include newly added items,
      // which would overwrite our optimistic add. onSuccess already updates the cache
      // with the real item ID - that's sufficient.

      // Invalidate the mine query for sidebar item_count updates (use user.id from AuthContext)
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.lists.mine(user.id) });
      }

      // Invalidate membership queries that include this media item
      // This ensures dropdowns re-check membership status
      queryClient.invalidateQueries({
        queryKey: ['lists', 'membership', mediaId, mediaType],
        exact: false,
      });
    },
  });
}

interface CreateWatchlistParams {
  name: string;
  description?: string;
  visibility?: 'public' | 'friends' | 'private';
  mediaFilter?: MediaFilter;
}

interface CreateWatchlistContext {
  previousData: WatchlistMeta[] | undefined;
}

/**
 * Mutation hook for creating new watchlists.
 * Implements optimistic updates with rollback on error.
 */
export function useCreateWatchlistMutation() {
  const { user, session } = useAuth();
  const token = session?.access_token;
  const queryClient = useQueryClient();

  return useMutation<WatchlistMeta, Error, CreateWatchlistParams, CreateWatchlistContext>({
    mutationFn: async ({ name, description, visibility = 'private', mediaFilter = 'mixed' }) => {
      if (!token) throw new Error('Not authenticated');

      const data = await apiFetch<{ watchlist: WatchlistMeta }>('/watchlists', {
        method: 'POST',
        token,
        body: {
          name,
          description,
          visibility,
          media_filter: mediaFilter,
        },
      });

      return data.watchlist;
    },

    onMutate: async ({ name, description, visibility = 'private', mediaFilter = 'mixed' }): Promise<CreateWatchlistContext> => {
      if (!user?.id) return { previousData: undefined };

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.lists.mine(user.id) });

      // Snapshot current data
      const previousData = queryClient.getQueryData<WatchlistMeta[]>(
        queryKeys.lists.mine(user.id)
      );

      // Optimistic update - add placeholder list
      const placeholderList: WatchlistMeta = {
        id: `temp-${Date.now()}`,
        name,
        slug: null,
        description: description ?? null,
        visibility,
        list_type: 'custom',
        is_default: false,
        item_count: 0,
        media_filter: mediaFilter,
        created_at: new Date().toISOString(),
        user_id: user.id,
      };

      queryClient.setQueryData<WatchlistMeta[]>(
        queryKeys.lists.mine(user.id),
        (old) => (old ? [...old, placeholderList] : [placeholderList])
      );

      return { previousData };
    },

    onError: (_err, _vars, context) => {
      // Rollback to snapshot
      if (context?.previousData && user?.id) {
        queryClient.setQueryData(
          queryKeys.lists.mine(user.id),
          context.previousData
        );
      }
    },

    onSettled: () => {
      // Always invalidate to get server data
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.lists.mine(user.id) });
      }
    },
  });
}

interface UpdateListItemNoteParams {
  listId: string;
  itemId: string;
  mediaId: number;
  mediaType: MediaType;
  note: string | null;
}

/**
 * Mutation hook for updating item notes.
 * Implements optimistic updates with rollback on error.
 * Uses cache helpers to update ALL sort variants.
 */
export function useUpdateListItemNoteMutation() {
  const { session } = useAuth();
  const token = session?.access_token;
  const queryClient = useQueryClient();

  return useMutation<void, Error, UpdateListItemNoteParams, { previousData: InfiniteItemsData | undefined }>({
    mutationFn: async ({ listId, itemId, note }) => {
      if (!token) throw new Error('Not authenticated');

      await apiFetch(`/watchlists/${listId}/items/${itemId}`, {
        method: 'PATCH',
        token,
        body: { notes: note },
      });
    },

    onMutate: async ({ listId, mediaId, mediaType, note }) => {
      await cancelAllListItemsQueries(queryClient, listId);

      const previousData = getAnyListItemsData(queryClient, listId);

      // Optimistic update - apply to ALL sort variants
      setAllListItemsData(queryClient, listId, (old) => {
        if (!old) return old;
        const newPages = old.pages.map((page) => ({
          ...page,
          items: page.items.map((i) =>
            i.media_id === mediaId && i.media_type === mediaType ? { ...i, notes: note } : i
          ),
        }));
        return { ...old, pages: newPages };
      });

      return { previousData };
    },

    onError: (_err, { listId }, context) => {
      if (context?.previousData) {
        setAllListItemsData(queryClient, listId, () => context.previousData);
      }
    },

    onSettled: (_data, _error, { listId }) => {
      invalidateAllListItemsQueries(queryClient, listId);
    },
  });
}

interface ReorderListItemsParams {
  listId: string;
  orderedItems: Array<{ id: string; position: number }>;
}

/**
 * Mutation hook for reordering list items (drag-and-drop).
 * Implements optimistic updates with rollback on error.
 * Preserves infinite query page structure.
 * Uses cache helpers to update ALL sort variants.
 */
export function useReorderListItemsMutation() {
  const { session } = useAuth();
  const token = session?.access_token;
  const queryClient = useQueryClient();

  return useMutation<void, Error, ReorderListItemsParams, { previousData: InfiniteItemsData | undefined }>({
    mutationFn: async ({ listId, orderedItems }) => {
      if (!token) throw new Error('Not authenticated');

      await apiFetch(`/watchlists/${listId}/reorder`, {
        method: 'PUT',
        token,
        body: { items: orderedItems },
      });
    },

    onMutate: async ({ listId, orderedItems }) => {
      await cancelAllListItemsQueries(queryClient, listId);

      const previousData = getAnyListItemsData(queryClient, listId);

      // Build a map of item id -> desired position from the mutation payload (O(n))
      const positionMap = new Map<string, number>();
      orderedItems.forEach(({ id, position }) => {
        positionMap.set(id, position);
      });

      // Optimistic update - reorder arrays + update positions for ALL sort variants
      setAllListItemsData(queryClient, listId, (old) => {
        if (!old || old.pages.length === 0) return old;

        // Flatten, reorder by desired position, re-split into pages
        const flat = old.pages.flatMap((page) => page.items);

        flat.sort((a, b) => {
          const aPos = positionMap.get(a.id) ?? Infinity;
          const bPos = positionMap.get(b.id) ?? Infinity;
          return aPos - bPos;
        });

        for (let i = 0; i < flat.length; i++) {
          flat[i] = { ...flat[i], position: i + 1 };
        }

        let offset = 0;
        const newPages = old.pages.map((page) => {
          const pageSize = page.items.length;
          const slice = flat.slice(offset, offset + pageSize);
          offset += pageSize;
          return { ...page, items: slice };
        });

        return { ...old, pages: newPages };
      });

      return { previousData };
    },

    onError: (_err, { listId }, context) => {
      if (context?.previousData) {
        setAllListItemsData(queryClient, listId, () => context.previousData);
      }
    },

    onSettled: (_data, _error, { listId }) => {
      invalidateAllListItemsQueries(queryClient, listId);
    },
  });
}

// ============================================================================
// useSetListItemDesiredMutation - SPAM-SAFE last-intent-wins mutation
// ============================================================================

/**
 * Per-item AbortController map key
 * Format: `${listId}:${mediaType}:${mediaId}`
 */
type ItemKey = `${string}:${MediaType}:${number}`;

function makeItemKey(listId: string, mediaType: MediaType, mediaId: number): ItemKey {
  return `${listId}:${mediaType}:${mediaId}`;
}

interface SetListItemDesiredParams {
  listId: string;
  mediaId: number;
  mediaType: MediaType;
  mediaTitle: string;
  mediaPoster?: string;
  mediaYear?: number;
  /** Explicit desired state: true = should be in list, false = should not be in list */
  desiredInList: boolean;
  /** Optional enrichment fields for instant metadata display */
  enrichment?: MediaEnrichment;
  // PRE-OPTIMISTIC snapshot fields (computed at call site BEFORE mutateAsync)
  // These MUST be used by mutationFn - NEVER read cache inside mutationFn
  /** Was item in list with confirmed server ID before this mutation? */
  wasInList: boolean;
  /** Was item a pending add (temp- ID) before this mutation? */
  wasPendingAdd: boolean;
  /** Confirmed server UUID if wasInList is true (for DELETE) */
  confirmedItemId?: string;
}

interface SetListItemDesiredResult {
  /** Whether an actual state change occurred (vs no-op or superseded) */
  didChange: boolean;
  /** The direction of the change if it occurred */
  direction?: 'added' | 'removed';
  /** The item ID (real server UUID on add, removed item ID on remove) */
  itemId?: string;
}

interface SetListItemDesiredContext {
  previousData: InfiniteItemsData | undefined;
  previousMembershipQueries: Array<[readonly unknown[], BatchMembershipResponse | undefined]>;
  /** Was this item actually in the list before the mutation? */
  wasInList: boolean;
  /** Track if this request was superseded by a newer one */
  abortController: AbortController;
  /** C3: Previous mine lists for rollback */
  previousMineLists: WatchlistMeta[] | undefined;
  /** C3: Previous optimistic guards for rollback */
  previousGuards: OptimisticGuards | undefined;
  /** C3: The count delta that was applied (for undo on didChange===false) */
  appliedDelta: number;
}

/**
 * SPAM-SAFE mutation hook for setting list membership to an explicit desired state.
 *
 * Key differences from toggle mutation:
 * - Takes explicit `desiredInList: boolean` - NOT toggle semantics
 * - Per-item AbortController map (useRef) - newer request supersedes older
 * - Returns `didChange: boolean` for toast correctness
 * - Allows removal of temp items (graceful no-op with cleanup)
 *
 * Supersede pattern:
 * 1. On mutate, check for existing AbortController for this item
 * 2. If exists, abort it (previous request is superseded)
 * 3. Create new AbortController for this request
 * 4. Pass signal to fetch - aborted requests resolve without error
 * 5. On success/error/settle, check if aborted before updating
 */
export function useSetListItemDesiredMutation() {
  const { session, user } = useAuth();
  const token = session?.access_token;
  const queryClient = useQueryClient();

  // Per-item AbortController map - useRef ensures no memory leak across renders
  // Garbage collected when component unmounts (unlike module-level Map)
  const abortControllersRef = useRef(new Map<ItemKey, AbortController>());

  return useMutation<
    SetListItemDesiredResult,
    Error,
    SetListItemDesiredParams,
    SetListItemDesiredContext
  >({
    mutationFn: async ({
      listId,
      mediaId,
      mediaType,
      mediaTitle,
      mediaPoster,
      mediaYear,
      desiredInList,
      // PRE-OPTIMISTIC snapshot - MUST use these, NEVER read cache here
      wasInList,
      wasPendingAdd,
      // Note: confirmedItemId no longer needed - DELETE by-media is idempotent without item_id
      confirmedItemId: _confirmedItemId,
    }) => {
      if (!token) throw new Error('Not authenticated');

      // Get the AbortController from the ref (set in onMutate before mutationFn runs)
      const itemKey = makeItemKey(listId, mediaType, mediaId);
      const abortController = abortControllersRef.current.get(itemKey);
      const signal = abortController?.signal;

      // INVARIANT: Use ONLY pre-snapshot variables to decide server calls
      // DO NOT read cache here - onMutate has already modified it optimistically

      if (desiredInList) {
        // ADD path
        if (wasInList) {
          // Already in list with confirmed ID - no-op
          return { didChange: false };
        }
        if (wasPendingAdd) {
          // Was pending add - POST will happen (or already happened)
          // This is a no-op since we're trying to add something already being added
          return { didChange: false };
        }

        // POST add to server
        const data = await apiFetch<{ item: WatchlistItemRow; duplicate?: boolean }>(
          `/watchlists/${listId}/items`,
          {
            method: 'POST',
            token,
            signal,
            body: {
              media_id: mediaId,
              media_type: mediaType,
              media_title: mediaTitle,
              media_poster: mediaPoster,
              media_year: mediaYear,
            },
          }
        );

        return {
          didChange: !data.duplicate,
          direction: 'added',
          itemId: data.item.id,
        };
      } else {
        // REMOVE path - single atomic idempotent endpoint (custom lists only)
        // DELETE by-media: no lookup needed, idempotent (deleted_count=0 = success)
        const data = await apiFetch<{ success: boolean; deleted_count: number }>(
          `/watchlists/${listId}/items/by-media`,
          {
            method: 'DELETE',
            token,
            signal,
            body: {
              media_id: mediaId,
              media_type: mediaType,
            },
          }
        );

        // didChange = true if any items were deleted (handles duplicates too)
        return {
          didChange: data.deleted_count > 0,
          direction: 'removed',
        };
      }
    },

    onMutate: async ({
      listId,
      mediaId,
      mediaType,
      mediaTitle,
      mediaPoster,
      mediaYear,
      desiredInList,
      enrichment,
      // PRE-OPTIMISTIC snapshot from call site - use these for count deltas
      wasInList,
      wasPendingAdd,
    }) => {
      const itemKey = makeItemKey(listId, mediaType, mediaId);

      // SUPERSEDE: Abort any existing request for this item
      const existingController = abortControllersRef.current.get(itemKey);
      if (existingController) {
        existingController.abort();
      }

      // Create new AbortController for this request
      const abortController = new AbortController();
      abortControllersRef.current.set(itemKey, abortController);

      // Cancel any outgoing refetches
      await cancelAllListItemsQueries(queryClient, listId);
      await queryClient.cancelQueries({
        queryKey: ['lists', 'membership', mediaId, mediaType],
        exact: false,
      });
      await queryClient.cancelQueries({
        queryKey: ['lists', 'membershipLookup', mediaId, mediaType],
        exact: false,
      });
      if (user?.id) {
        await queryClient.cancelQueries({ queryKey: queryKeys.lists.mine(user.id) });
      }

      // Snapshot current cache state for rollback (but use PRE-snapshot from variables for logic)
      const previousData = getAnyListItemsData(queryClient, listId);

      // Snapshot membership queries for rollback
      const previousMembershipQueries = queryClient.getQueriesData<BatchMembershipResponse>({
        queryKey: ['lists', 'membership', mediaId, mediaType],
        exact: false,
      });

      // Optimistic update: set cache to desired state (variant-aware + last-page-aware)
      setAllListItemsDataWithSortInfo(queryClient, listId, (old, sortParams) => {
        // Seed minimal cache if not present
        if (!old) {
          old = {
            pages: [{
              items: [],
              pagination: { page: 1, limit: 100, total: 0, has_more: false },
              watchlist: null,
            }],
            pageParams: [1],
          };
        }

        if (desiredInList) {
          // ADD: Insert if not present (or keep existing)
          const alreadyExists = old.pages.some((p) =>
            p.items.some((i) => i.media_id === mediaId && i.media_type === mediaType)
          );

          if (alreadyExists) {
            return old; // Already in desired state
          }

          const optimisticItem: WatchlistItemRow = {
            id: `temp-${Date.now()}`,
            watchlist_id: listId,
            media_id: mediaId,
            media_type: mediaType,
            media_title: mediaTitle,
            media_poster: mediaPoster ?? null,
            media_year: mediaYear ?? null,
            notes: null,
            position: 999999,
            added_at: new Date().toISOString(),
            genre_ids: enrichment?.genre_ids,
            runtime: enrichment?.runtime,
            imdb_rating: enrichment?.imdb_rating,
            rt_rating: enrichment?.rt_rating,
            filmpl_rating: enrichment?.filmpl_rating,
            popularity: enrichment?.popularity,
            original_language: enrichment?.original_language,
            vote_count: enrichment?.vote_count,
          };

          // Variant-aware + last-page-aware placement
          const shouldAppend =
            sortParams.sortBy === 'position' ||
            (sortParams.sortBy === 'added_at' && sortParams.sortOrder === 'asc');

          const lastIdx = old.pages.length - 1;
          const lastPageFullyLoaded = !old.pages[lastIdx]?.pagination.has_more;

          const newPages = old.pages.map((page, idx) => {
            let newItems = page.items;

            if (shouldAppend) {
              if (idx === lastIdx && lastPageFullyLoaded) {
                newItems = [...page.items, optimisticItem];
              }
            } else {
              if (idx === 0) {
                newItems = [optimisticItem, ...page.items];
              }
            }

            const updatedWatchlist = idx === 0 && page.watchlist
              ? { ...page.watchlist, item_count: (page.watchlist.item_count || 0) + 1 }
              : page.watchlist;

            return { ...page, items: newItems, watchlist: updatedWatchlist };
          });
          return { ...old, pages: newPages };
        } else {
          // REMOVE: Filter out the item
          const newPages = old.pages.map((page, idx) => {
            const filtered = page.items.filter(
              (i) => !(i.media_id === mediaId && i.media_type === mediaType)
            );
            const removed = filtered.length < page.items.length;
            return {
              ...page,
              items: filtered,
              watchlist:
                removed && page.watchlist && idx === 0
                  ? { ...page.watchlist, item_count: Math.max(0, (page.watchlist.item_count || 0) - 1) }
                  : page.watchlist,
            };
          });
          return { ...old, pages: newPages };
        }
      });

      // Update membership batch cache
      queryClient.setQueriesData<BatchMembershipResponse>(
        { queryKey: ['lists', 'membership', mediaId, mediaType], exact: false },
        (old) => {
          const currentIds = old?.in_list_ids ?? [];
          if (desiredInList) {
            if (!currentIds.includes(listId)) {
              return { in_list_ids: [...currentIds, listId] };
            }
          } else {
            return { in_list_ids: currentIds.filter((id) => id !== listId) };
          }
          return old;
        }
      );

      // Update mine query item_count
      const userId = user?.id;
      let previousMineLists: WatchlistMeta[] | undefined;
      let previousGuards: OptimisticGuards | undefined;
      let appliedDelta = 0;

      if (userId) {
        // C3: Snapshot previous mine lists for rollback
        previousMineLists = queryClient.getQueryData<WatchlistMeta[]>(queryKeys.lists.mine(userId));
        previousGuards = queryClient.getQueryData<OptimisticGuards>(getOptimisticGuardsKey(userId));

        let listsData = previousMineLists;
        if (!listsData) {
          const cached = getMyListsFromCache(userId);
          if (cached?.lists) {
            listsData = cached.lists;
            queryClient.setQueryData(queryKeys.lists.mine(userId), listsData);
          }
        }

        if (listsData) {
          const currentList = listsData.find((l) => l.id === listId);
          const currentCount = currentList?.item_count ?? 0;
          const currentServerUpdatedAt = currentList?.updated_at;

          // Compute new count based on desired state vs PRE-OPTIMISTIC state from variables
          let delta = 0;
          if (desiredInList && !wasInList && !wasPendingAdd) delta = 1;
          if (!desiredInList && (wasInList || wasPendingAdd)) delta = -1;
          appliedDelta = delta;

          const newCount = Math.max(0, currentCount + delta);

          queryClient.setQueryData<WatchlistMeta[]>(
            queryKeys.lists.mine(userId),
            (old) => {
              if (!old) return old;
              return old.map((list) =>
                list.id === listId ? { ...list, item_count: newCount } : list
              );
            }
          );

          if (delta !== 0) {
            setOptimisticGuard(queryClient, userId, listId, newCount, currentServerUpdatedAt);
          }

          // C2: Sync localStorage mirror with React Query (single source of truth)
          const updatedLists = queryClient.getQueryData<WatchlistMeta[]>(queryKeys.lists.mine(userId));
          if (updatedLists) {
            setMyListsCache(userId, updatedLists);
          }
        }
      }

      return {
        previousData,
        previousMembershipQueries,
        wasInList,
        abortController,
        previousMineLists,
        previousGuards,
        appliedDelta,
      };
    },

    onError: (err, { listId }, context) => {
      // Check if this was an abort (superseded by newer request)
      if (err.name === 'AbortError' || context?.abortController.signal.aborted) {
        // Superseded - don't rollback, the newer mutation handles state
        return;
      }

      // Real error - rollback list items
      if (context?.previousData) {
        setAllListItemsData(queryClient, listId, () => context.previousData);
      }

      // Rollback membership queries
      if (context?.previousMembershipQueries) {
        for (const [key, data] of context.previousMembershipQueries) {
          queryClient.setQueryData(key, data);
        }
      }

      // C3: Rollback mine lists + guards + localStorage
      if (user?.id) {
        if (context?.previousMineLists) {
          queryClient.setQueryData(queryKeys.lists.mine(user.id), context.previousMineLists);
          setMyListsCache(user.id, context.previousMineLists);
        }
        if (context?.previousGuards !== undefined) {
          queryClient.setQueryData(getOptimisticGuardsKey(user.id), context.previousGuards);
        }
      }

      invalidateAllListItemsQueries(queryClient, listId);
    },

    onSuccess: (data, { listId, mediaId, mediaType }, context) => {
      // Check if superseded
      if (context?.abortController.signal.aborted) {
        return; // Newer request takes precedence
      }

      // If we added and got real item back, update cache with server ID
      if (data.direction === 'added' && data.itemId) {
        setAllListItemsData(queryClient, listId, (old) => {
          if (!old) return old;
          const newPages = old.pages.map((page) => ({
            ...page,
            items: page.items.map((i) => {
              if (i.media_id === mediaId && i.media_type === mediaType && i.id.startsWith('temp-')) {
                return { ...i, id: data.itemId! };
              }
              return i;
            }),
          }));
          return { ...old, pages: newPages };
        });
      }

      // C3: If didChange===false (duplicate add / deleted_count=0 remove),
      // undo any optimistic mine delta + clear guard + sync localStorage
      if (!data.didChange && user?.id && context?.appliedDelta !== 0) {
        // Undo the delta we applied optimistically
        queryClient.setQueryData<WatchlistMeta[]>(
          queryKeys.lists.mine(user.id),
          (old) => {
            if (!old) return old;
            return old.map((list) =>
              list.id === listId
                ? { ...list, item_count: Math.max(0, (list.item_count || 0) - (context.appliedDelta || 0)) }
                : list
            );
          }
        );

        // Clear the guard for this list
        clearOptimisticGuard(queryClient, user.id, listId);

        // Sync localStorage from current mine query
        const updatedLists = queryClient.getQueryData<WatchlistMeta[]>(queryKeys.lists.mine(user.id));
        if (updatedLists) {
          setMyListsCache(user.id, updatedLists);
        }
      }
    },

    onSettled: (_data, _err, { listId, mediaId, mediaType }, context) => {
      // Check if superseded
      if (context?.abortController.signal.aborted) {
        return; // Newer request handles cleanup
      }

      // Clean up AbortController from map
      const itemKey = makeItemKey(listId, mediaType, mediaId);
      const currentController = abortControllersRef.current.get(itemKey);
      if (currentController === context?.abortController) {
        abortControllersRef.current.delete(itemKey);
      }

      // Invalidate for convergence
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.lists.mine(user.id) });
      }
      queryClient.invalidateQueries({
        queryKey: ['lists', 'membership', mediaId, mediaType],
        exact: false,
      });
    },
  });
}

// ============================================================================
// Cache Invalidation Hooks (for default list mutations from UserLibraryContext)
// ============================================================================

/**
 * Hook to get cache invalidation functions for default lists.
 * UserLibraryContext should call these after successful mutations to keep RQ in sync.
 */
export function useDefaultListCacheInvalidation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const invalidateDefaultListItems = async (slug: 'watchlist' | 'watched' | 'favorites') => {
    // Find the default list ID from the mine query
    const lists = queryClient.getQueryData<WatchlistMeta[]>(
      queryKeys.lists.mine(user?.id || '')
    );
    const defaultList = lists?.find((l) => l.slug === slug && l.is_default);

    if (defaultList) {
      invalidateAllListItemsQueries(queryClient, defaultList.id);
    }

    // Also invalidate the mine query for item_count updates
    if (user?.id) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.lists.mine(user.id) });
    }
  };

  return { invalidateDefaultListItems };
}

// ============================================================================
// Phase 3: Watchlist Management Mutations
// ============================================================================

// ----------------------------------------------------------------------------
// Types for new hooks
// ----------------------------------------------------------------------------

export interface Collaborator {
  user_id: string;
  role: CollaboratorRole;
  added_at: string;
  username?: string;
  display_name?: string;
  avatar_url?: string | null;
}

export interface UserSearchResult {
  id: string;
  username: string;
  display_name?: string;
  avatar_url?: string | null;
}

interface DeletedWatchlistsResponse {
  watchlists: WatchlistMeta[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

interface CollaboratorsResponse {
  collaborators: Collaborator[];
}

interface UserSearchResponse {
  users: UserSearchResult[];
}

// ----------------------------------------------------------------------------
// useUpdateWatchlistMutation - PATCH /watchlists/:id
// ----------------------------------------------------------------------------

interface UpdateWatchlistParams {
  listId: string;
  name?: string;
  description?: string | null;
  visibility?: 'public' | 'friends' | 'private';
  mediaFilter?: MediaFilter;
  coverUrl?: string | null; // null to remove cover
}

interface UpdateWatchlistContext {
  previousMine: WatchlistMeta[] | undefined;
  previousDetail: WatchlistDetailResponse | undefined;
}

/**
 * Mutation hook for updating watchlist metadata (name, description, visibility, media_filter, cover_url).
 * Default lists can only update visibility - name is immutable.
 * Pass coverUrl: null to remove the cover image.
 *
 * Invalidates:
 * - lists.mine(userId) - Sidebar reflects new name/visibility
 * - lists.detail(listId) - Detail page reflects changes
 * - lists.itemsBase(listId) - Embedded watchlist metadata in items query
 */
export function useUpdateWatchlistMutation() {
  const { user, session } = useAuth();
  const token = session?.access_token;
  const queryClient = useQueryClient();

  return useMutation<WatchlistMeta, Error, UpdateWatchlistParams, UpdateWatchlistContext>({
    mutationFn: async ({ listId, name, description, visibility, mediaFilter, coverUrl }) => {
      if (!token) throw new Error('Not authenticated');

      const body: Record<string, unknown> = {};
      if (name !== undefined) body.name = name;
      if (description !== undefined) body.description = description;
      if (visibility !== undefined) body.visibility = visibility;
      if (mediaFilter !== undefined) body.media_filter = mediaFilter;
      if (coverUrl !== undefined) body.cover_url = coverUrl;

      const data = await apiFetch<{ watchlist: WatchlistMeta }>(`/watchlists/${listId}`, {
        method: 'PATCH',
        token,
        body,
      });

      return data.watchlist;
    },

    onMutate: async ({ listId, name, description, visibility, mediaFilter, coverUrl }) => {
      if (!user?.id) return { previousMine: undefined, previousDetail: undefined };

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.lists.mine(user.id) });
      await queryClient.cancelQueries({ queryKey: queryKeys.lists.detail(listId) });

      // Snapshot current data
      const previousMine = queryClient.getQueryData<WatchlistMeta[]>(
        queryKeys.lists.mine(user.id)
      );
      const previousDetail = queryClient.getQueryData<WatchlistDetailResponse>(
        queryKeys.lists.detail(listId)
      );

      // Optimistic update - mine query
      queryClient.setQueryData<WatchlistMeta[]>(
        queryKeys.lists.mine(user.id),
        (old) => {
          if (!old) return old;
          return old.map((list) => {
            if (list.id !== listId) return list;
            return {
              ...list,
              ...(name !== undefined && { name }),
              ...(description !== undefined && { description }),
              ...(visibility !== undefined && { visibility }),
              ...(mediaFilter !== undefined && { media_filter: mediaFilter }),
              ...(coverUrl !== undefined && { cover_url: coverUrl }),
            };
          });
        }
      );

      // Optimistic update - detail query
      queryClient.setQueryData<WatchlistDetailResponse>(
        queryKeys.lists.detail(listId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            watchlist: {
              ...old.watchlist,
              ...(name !== undefined && { name }),
              ...(description !== undefined && { description }),
              ...(visibility !== undefined && { visibility }),
              ...(mediaFilter !== undefined && { media_filter: mediaFilter }),
              ...(coverUrl !== undefined && { cover_url: coverUrl }),
            },
          };
        }
      );

      return { previousMine, previousDetail };
    },

    onError: (_err, { listId }, context) => {
      // Rollback on error
      if (context?.previousMine && user?.id) {
        queryClient.setQueryData(queryKeys.lists.mine(user.id), context.previousMine);
      }
      if (context?.previousDetail) {
        queryClient.setQueryData(queryKeys.lists.detail(listId), context.previousDetail);
      }
    },

    onSettled: (_data, _error, { listId }) => {
      // Always invalidate to get server data
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.lists.mine(user.id) });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.lists.detail(listId) });
      invalidateAllListItemsQueries(queryClient, listId);
    },
  });
}

// ----------------------------------------------------------------------------
// useDeleteWatchlistMutation - DELETE /watchlists/:id (soft delete)
// ----------------------------------------------------------------------------

interface DeleteWatchlistParams {
  listId: string;
}

interface DeleteWatchlistContext {
  previousMine: WatchlistMeta[] | undefined;
  deletedList: WatchlistMeta | undefined;
}

/**
 * Mutation hook for soft-deleting a watchlist (Spotify-style).
 * Sets deleted_at on server - list moves to "Deleted Lists" recovery page.
 * Default lists CANNOT be deleted (enforced server-side).
 *
 * Invalidates:
 * - lists.mine(userId) - List disappears from sidebar
 * - lists.deletedBase(userId) - List appears in deleted lists
 */
export function useDeleteWatchlistMutation() {
  const { user, session } = useAuth();
  const token = session?.access_token;
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, DeleteWatchlistParams, DeleteWatchlistContext>({
    mutationFn: async ({ listId }) => {
      if (!token) throw new Error('Not authenticated');

      await apiFetch(`/watchlists/${listId}`, {
        method: 'DELETE',
        token,
      });

      return { success: true };
    },

    onMutate: async ({ listId }) => {
      if (!user?.id) return { previousMine: undefined, deletedList: undefined };

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.lists.mine(user.id) });

      // Snapshot current data
      const previousMine = queryClient.getQueryData<WatchlistMeta[]>(
        queryKeys.lists.mine(user.id)
      );

      // Find the list being deleted (for potential undo)
      const deletedList = previousMine?.find((l) => l.id === listId);

      // Optimistic update - remove from mine query
      queryClient.setQueryData<WatchlistMeta[]>(
        queryKeys.lists.mine(user.id),
        (old) => (old ? old.filter((l) => l.id !== listId) : old)
      );

      return { previousMine, deletedList };
    },

    onError: (_err, _vars, context) => {
      // Rollback on error
      if (context?.previousMine && user?.id) {
        queryClient.setQueryData(queryKeys.lists.mine(user.id), context.previousMine);
      }
    },

    onSettled: () => {
      // Always invalidate to get server data
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.lists.mine(user.id) });
        queryClient.invalidateQueries({ queryKey: queryKeys.lists.deletedBase(user.id) });
      }
    },
  });
}

// ----------------------------------------------------------------------------
// useDeletedWatchlistsQuery - GET /watchlists/deleted?page=&limit=
// ----------------------------------------------------------------------------

interface UseDeletedWatchlistsOptions {
  page?: number;
  limit?: number;
  enabled?: boolean;
}

interface UseDeletedWatchlistsResult {
  watchlists: WatchlistMeta[];
  pagination: { page: number; limit: number; total: number };
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Query hook for fetching soft-deleted watchlists (Spotify-style recovery).
 * Only returns lists owned by the current user with deleted_at IS NOT NULL.
 */
export function useDeletedWatchlistsQuery(
  options: UseDeletedWatchlistsOptions = {}
): UseDeletedWatchlistsResult {
  const { user, session } = useAuth();
  const token = session?.access_token;
  const userId = user?.id;
  const { page = 1, limit = 20, enabled = true } = options;

  const query = useQuery({
    queryKey: queryKeys.lists.deleted(userId || '', { page, limit }),
    queryFn: async () => {
      if (!token || !userId) {
        return { watchlists: [], pagination: { page: 1, limit: 20, total: 0 } };
      }

      const data = await apiFetch<DeletedWatchlistsResponse>(
        `/watchlists/deleted?page=${page}&limit=${limit}`,
        { token }
      );

      return data;
    },
    enabled: enabled && !!userId && !!token,
    staleTime: 30_000, // 30 seconds
  });

  return {
    watchlists: query.data?.watchlists ?? [],
    pagination: query.data?.pagination ?? { page: 1, limit: 20, total: 0 },
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    refetch: async () => { await query.refetch(); },
  };
}

// ----------------------------------------------------------------------------
// useRestoreWatchlistMutation - PATCH /watchlists/:id/restore
// ----------------------------------------------------------------------------

interface RestoreWatchlistParams {
  listId: string;
}

/**
 * Mutation hook for restoring a soft-deleted watchlist.
 * Clears deleted_at on server - list reappears in sidebar.
 *
 * Invalidates:
 * - lists.mine(userId) - List reappears in sidebar
 * - lists.deletedBase(userId) - List disappears from deleted lists
 */
export function useRestoreWatchlistMutation() {
  const { user, session } = useAuth();
  const token = session?.access_token;
  const queryClient = useQueryClient();

  return useMutation<WatchlistMeta, Error, RestoreWatchlistParams>({
    mutationFn: async ({ listId }) => {
      if (!token) throw new Error('Not authenticated');

      const data = await apiFetch<{ watchlist: WatchlistMeta }>(
        `/watchlists/${listId}/restore`,
        { method: 'PATCH', token }
      );

      return data.watchlist;
    },

    onSettled: () => {
      // Invalidate both mine and deleted queries
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.lists.mine(user.id) });
        queryClient.invalidateQueries({ queryKey: queryKeys.lists.deletedBase(user.id) });
      }
    },
  });
}

// ----------------------------------------------------------------------------
// useUploadWatchlistCoverMutation - POST /watchlists/:id/cover (multipart)
// ----------------------------------------------------------------------------

interface UploadWatchlistCoverParams {
  listId: string;
  file: File;
}

interface UploadWatchlistCoverContext {
  previousMine: WatchlistMeta[] | undefined;
  previousDetail: WatchlistDetailResponse | undefined;
}

/**
 * Mutation hook for uploading a custom cover image for a watchlist.
 * Uploads to R2 via worker, then persists cover_url to DB via PATCH.
 * Default lists CANNOT have custom covers (enforced server-side).
 *
 * Flow:
 * 1. POST /watchlists/:id/cover (multipart, field="cover") -> { url }
 * 2. PATCH /watchlists/:id with { cover_url: url } to persist
 *
 * Invalidates:
 * - lists.mine(userId) - Sidebar card shows new cover
 * - lists.detail(listId) - Detail page shows new cover
 * - lists.itemsBase(listId) - Embedded watchlist metadata
 */
export function useUploadWatchlistCoverMutation() {
  const { user, session } = useAuth();
  const token = session?.access_token;
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean; url: string }, Error, UploadWatchlistCoverParams, UploadWatchlistCoverContext>({
    mutationFn: async ({ listId, file }) => {
      if (!token) throw new Error('Not authenticated');

      // Step 1: Upload to R2 via multipart
      const formData = new FormData();
      formData.append('cover', file); // Field name must be "cover"

      const baseUrl = import.meta.env.VITE_API_URL || '';
      const uploadResponse = await fetch(`${baseUrl}/watchlists/${listId}/cover`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({}));
        throw new Error(errorData.error || `Upload failed: ${uploadResponse.status}`);
      }

      const uploadResult = await uploadResponse.json() as { success: boolean; url: string };

      // Step 2: Persist cover_url to DB via PATCH
      await apiFetch(`/watchlists/${listId}`, {
        method: 'PATCH',
        token,
        body: { cover_url: uploadResult.url },
      });

      return uploadResult;
    },

    onMutate: async ({ listId }) => {
      if (!user?.id) return { previousMine: undefined, previousDetail: undefined };

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.lists.mine(user.id) });
      await queryClient.cancelQueries({ queryKey: queryKeys.lists.detail(listId) });

      // Snapshot current data (no optimistic update for upload - wait for real URL)
      const previousMine = queryClient.getQueryData<WatchlistMeta[]>(
        queryKeys.lists.mine(user.id)
      );
      const previousDetail = queryClient.getQueryData<WatchlistDetailResponse>(
        queryKeys.lists.detail(listId)
      );

      return { previousMine, previousDetail };
    },

    onSuccess: (data, { listId }) => {
      // Update caches with persisted cover URL
      if (user?.id) {
        queryClient.setQueryData<WatchlistMeta[]>(
          queryKeys.lists.mine(user.id),
          (old) => {
            if (!old) return old;
            return old.map((list) =>
              list.id === listId ? { ...list, cover_url: data.url } : list
            );
          }
        );
      }

      queryClient.setQueryData<WatchlistDetailResponse>(
        queryKeys.lists.detail(listId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            watchlist: { ...old.watchlist, cover_url: data.url },
          };
        }
      );
    },

    onError: (_err, { listId }, context) => {
      // Rollback on error (restore previous state)
      if (context?.previousMine && user?.id) {
        queryClient.setQueryData(queryKeys.lists.mine(user.id), context.previousMine);
      }
      if (context?.previousDetail) {
        queryClient.setQueryData(queryKeys.lists.detail(listId), context.previousDetail);
      }
    },

    onSettled: (_data, _error, { listId }) => {
      // Always invalidate to get server data
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.lists.mine(user.id) });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.lists.detail(listId) });
      invalidateAllListItemsQueries(queryClient, listId);
    },
  });
}

// ----------------------------------------------------------------------------
// useWatchlistCollaboratorsQuery - GET /watchlists/:id/collaborators
// ----------------------------------------------------------------------------

interface UseWatchlistCollaboratorsOptions {
  enabled?: boolean;
}

interface UseWatchlistCollaboratorsResult {
  collaborators: Collaborator[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Query hook for fetching collaborators of a watchlist.
 * Only owner can view collaborators (enforced by RLS).
 */
export function useWatchlistCollaboratorsQuery(
  listId: string | undefined,
  options: UseWatchlistCollaboratorsOptions = {}
): UseWatchlistCollaboratorsResult {
  const { session } = useAuth();
  const token = session?.access_token;
  const { enabled = true } = options;

  const query = useQuery({
    queryKey: queryKeys.lists.collaborators(listId || ''),
    queryFn: async () => {
      if (!token || !listId) {
        return { collaborators: [] };
      }

      const data = await apiFetch<CollaboratorsResponse>(
        `/watchlists/${listId}/collaborators`,
        { token }
      );

      return data;
    },
    enabled: enabled && !!listId && !!token,
    staleTime: 60_000, // 1 minute
  });

  return {
    collaborators: query.data?.collaborators ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    refetch: async () => { await query.refetch(); },
  };
}

// ----------------------------------------------------------------------------
// useAddCollaboratorMutation - POST /watchlists/:id/collaborators
// ----------------------------------------------------------------------------

interface AddCollaboratorParams {
  listId: string;
  userId: string;
  role: CollaboratorRole;
}

/**
 * Mutation hook for adding a collaborator to a watchlist.
 * Only owner can add collaborators.
 * Default lists CANNOT have collaborators (enforced server-side).
 *
 * Invalidates:
 * - lists.collaborators(listId) - New collaborator appears in list
 * - lists.mine(collaboratorUserId) - List appears in their sidebar as collab
 */
export function useAddCollaboratorMutation() {
  const { session } = useAuth();
  const token = session?.access_token;
  const queryClient = useQueryClient();

  return useMutation<Collaborator, Error, AddCollaboratorParams>({
    mutationFn: async ({ listId, userId, role }) => {
      if (!token) throw new Error('Not authenticated');

      const data = await apiFetch<{ collaborator: Collaborator }>(
        `/watchlists/${listId}/collaborators`,
        {
          method: 'POST',
          token,
          body: { user_id: userId, role },
        }
      );

      return data.collaborator;
    },

    onSettled: (_data, _error, { listId, userId }) => {
      // Invalidate collaborators list
      queryClient.invalidateQueries({ queryKey: queryKeys.lists.collaborators(listId) });
      // Invalidate the added user's mine query (list now appears in their sidebar)
      queryClient.invalidateQueries({ queryKey: queryKeys.lists.mine(userId) });
    },
  });
}

// ----------------------------------------------------------------------------
// useUpdateCollaboratorRoleMutation - PATCH /watchlists/:id/collaborators/:userId
// ----------------------------------------------------------------------------

interface UpdateCollaboratorRoleParams {
  listId: string;
  userId: string;
  role: CollaboratorRole;
}

/**
 * Mutation hook for updating a collaborator's role.
 * Only owner can update roles.
 *
 * Invalidates:
 * - lists.collaborators(listId) - Role change reflected in list
 */
export function useUpdateCollaboratorRoleMutation() {
  const { session } = useAuth();
  const token = session?.access_token;
  const queryClient = useQueryClient();

  return useMutation<Collaborator, Error, UpdateCollaboratorRoleParams>({
    mutationFn: async ({ listId, userId, role }) => {
      if (!token) throw new Error('Not authenticated');

      const data = await apiFetch<{ collaborator: Collaborator }>(
        `/watchlists/${listId}/collaborators/${userId}`,
        {
          method: 'PATCH',
          token,
          body: { role },
        }
      );

      return data.collaborator;
    },

    onMutate: async ({ listId, userId, role }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.lists.collaborators(listId) });

      // Optimistic update
      queryClient.setQueryData<CollaboratorsResponse>(
        queryKeys.lists.collaborators(listId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            collaborators: old.collaborators.map((c) =>
              c.user_id === userId ? { ...c, role } : c
            ),
          };
        }
      );
    },

    onSettled: (_data, _error, { listId }) => {
      // Always invalidate to get server data
      queryClient.invalidateQueries({ queryKey: queryKeys.lists.collaborators(listId) });
    },
  });
}

// ----------------------------------------------------------------------------
// useRemoveCollaboratorMutation - DELETE /watchlists/:id/collaborators/:userId
// ----------------------------------------------------------------------------

interface RemoveCollaboratorParams {
  listId: string;
  userId: string;
}

/**
 * Mutation hook for removing a collaborator from a watchlist.
 * Only owner can remove collaborators.
 *
 * Invalidates:
 * - lists.collaborators(listId) - Collaborator disappears from list
 * - lists.mine(removedUserId) - List disappears from their sidebar
 */
export function useRemoveCollaboratorMutation() {
  const { session } = useAuth();
  const token = session?.access_token;
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, RemoveCollaboratorParams>({
    mutationFn: async ({ listId, userId }) => {
      if (!token) throw new Error('Not authenticated');

      await apiFetch(`/watchlists/${listId}/collaborators/${userId}`, {
        method: 'DELETE',
        token,
      });

      return { success: true };
    },

    onMutate: async ({ listId, userId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.lists.collaborators(listId) });

      // Optimistic update - remove from collaborators list
      queryClient.setQueryData<CollaboratorsResponse>(
        queryKeys.lists.collaborators(listId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            collaborators: old.collaborators.filter((c) => c.user_id !== userId),
          };
        }
      );
    },

    onSettled: (_data, _error, { listId, userId }) => {
      // Invalidate collaborators list
      queryClient.invalidateQueries({ queryKey: queryKeys.lists.collaborators(listId) });
      // Invalidate the removed user's mine query (list no longer in their sidebar)
      queryClient.invalidateQueries({ queryKey: queryKeys.lists.mine(userId) });
    },
  });
}

// ----------------------------------------------------------------------------
// useUserSearchQuery - GET /users/search?q=&limit=
// ----------------------------------------------------------------------------

interface UseUserSearchOptions {
  limit?: number;
  enabled?: boolean;
}

interface UseUserSearchResult {
  users: UserSearchResult[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
}

/**
 * Query hook for searching users (for collaborator picker).
 * Uses debounced query - caller should debounce the query string.
 * Returns matching users by username/display_name.
 * Viewer-scoped: cache is keyed by viewer to prevent cross-user leakage.
 */
export function useUserSearchQuery(
  query: string,
  options: UseUserSearchOptions = {}
): UseUserSearchResult {
  const { user, session } = useAuth();
  const token = session?.access_token;
  const viewerBucket = user?.id ?? 'anon';
  const { limit = 10, enabled = true } = options;

  const normalizedQuery = query.toLowerCase().trim();
  const hasQuery = normalizedQuery.length >= 2; // Minimum 2 chars

  const queryResult = useQuery({
    queryKey: queryKeys.users.search(viewerBucket, normalizedQuery),
    queryFn: async () => {
      if (!token || !hasQuery) {
        return { users: [] };
      }

      const data = await apiFetch<UserSearchResponse>(
        `/users/search?q=${encodeURIComponent(normalizedQuery)}&limit=${limit}`,
        { token }
      );

      return data;
    },
    enabled: enabled && !!token && hasQuery,
    staleTime: 60_000, // 1 minute
  });

  return {
    users: queryResult.data?.users ?? [],
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    error: queryResult.error as Error | null,
  };
}
